<script lang="ts">
import {defineComponent, h, shallowRef} from 'vue';
import {FetchRetryer} from '../fetchretryer.ts';
import {timing} from '../analytics.ts';
import {SVG} from './svg_utils.ts';
import type {Container} from '../container.ts';

class ResponseError extends Error {
  response: Response;

  /**
   * @param {string} message
   * @param {Response} response
   */
  constructor(message: string, response: Response) {
    super(message);
    /** @type {Response} */
    this.response = response;
  }
}

/**
 * Remove attributes that are false according to Vue.
 * @param {object} attrs
 * @returns {object}
 */
function filterAttrs(attrs: Record<string, unknown>) {
  const result: Record<string, unknown> = {};
  for (const key of Object.keys(attrs)) {
    if (attrs[key] !== null && attrs[key] !== undefined) {
      result[key] = attrs[key];
    }
  }
  return result;
}

/**
 * Convert element attributes to a plain object.
 * @param {Element} elem
 * @returns {object}
 */
function attrsToObject(elem: Element) {
  const obj: Record<string, string> = {};
  const attrs = elem.attributes;
  if (!attrs) {
    return obj;
  }
  const len = attrs.length;
  for (let i = 0; i < len; i++) {
    const attr = attrs[i];
    obj[attr.name] = attr.value;
  }
  return obj;
}

const observerSupported = window && 'IntersectionObserver' in window;

export type GeneratedInlineSvgData = {
  fetchRetryer: FetchRetryer;
  viewBox?: {width: number; height: number};
  children?: Blob;
  observer?: IntersectionObserver;
  startTime?: number;
};

/**
 * A Vue component that fetches an SVG and inlines it in the DOM.  It assumes
 * that the source URL is a generated SVG that may return a 503 response.  If
 * that occurs, the fetch is retried.
 *
 * This also uses the dependency injection container's FetchRetryer, meaning
 * that the requests are shared across all instances and successful responses
 * are cached.
 */
export default defineComponent({
  name: 'GeneratedInlineSvg',
  inject: {
    untypedContainer: {from: 'container'},
  },
  inheritAttrs: false,
  props: {
    src: {
      type: String,
      required: true,
    },
    // If your function mutates the SVGElement container, without the body, it should modify it here.
    transformContainer: {
      type: Function,
      default: (svg: SVGElement) => svg,
    },
    // If your function mutates the SVGElement, it should clone it and return
    // that.
    transformSource: {
      type: Function,
      default: (svg: SVGElement) => svg,
    },
    // Transform the blob children before rendering it to the DOM
    transformChildren: {
      type: Function,
      default: (blob: Blob) => blob,
    },

  },

  emits: ['error', 'load'],

  setup() {
    const svgProperties = shallowRef(null as Record<string, unknown> | null);
    return {svgProperties};
  },

  data(): GeneratedInlineSvgData {
    return {
      // Vue removes the shallowRef, normally you'd want to put this in setup, but this needs access to container so we can't
      // Will be cleaned up by converting component to Composition API
      fetchRetryer: shallowRef(FetchRetryer.for(this.untypedContainer as unknown as Container, this.src, {skipCache: true})) as unknown as FetchRetryer,
    };
  },

  computed: {
    container() {
      return this.untypedContainer as unknown as Container;
    },
  },

  watch: {
    src() {
      this.fetchRetryer = FetchRetryer.for(this.container, this.src, {skipCache: true});
      this.startFetching();
    },
  },

  created() {
    // eslint-disable-next-line github/no-then
    this.fetchRetryer.promise.then(this.onLoad.bind(this),
      this.onError.bind(this));
  },

  mounted() {
    // If observer is supported, wait until this component is in view
    // before loading data. This prioritizes visible components for
    // display first, and allows us to save memory on large diffs.
    if (observerSupported) {
      this.observer = new IntersectionObserver((entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            this.observer.disconnect();
            this.startFetching();
          }
        }
      });
      this.observer.observe(this.$parent.$el);
      // Fix for Firefox, which only runs IntersectionObserver when a RAF happens.
      requestAnimationFrame(() => {});
    } else {
      this.startFetching();
    }
  },

  unmounted() {
    if (this.observer) {
      this.observer.disconnect();
    }
  },

  // If we updated the element, add the cached children
  updated() {
    if (this.svgProperties && this.children) {
      this.transformContainer(this.$el);

      (async () => {
        const children = await this.transformChildren(this.children);

        // Prefer the newer API
        if (this.$el.setHTMLUnsafe) {
          this.$el.setHTMLUnsafe(await children.text());
        } else {
          this.$el.innerHTML = await children.text();
        }
        this.children = null;

        await this.transformSource(this.$el);

        this.$emit('load', this.$el);

        if (this.startTime) {
          timing('svg_render', performance.now() - this.startTime, {
            src: this.src,
          });
          this.startTime = null;
        }
      })();
    }
  },

  methods: {
    aspectRatio() {
      if (!this.viewBox) {
        return null;
      }
      return this.viewBox.width / this.viewBox.height;
    },
    /**
     * @param {FetchRetryer} fetchRetryer
     */
    async onLoad(fetchRetryer: FetchRetryer) {
      const response = fetchRetryer.response;
      if (!response) {
        throw new Error(`response should not be null: ${this.src}`);
      }

      performance.mark('svg_loaded');

      if (response.status >= 200 && response.status < 400) {
        const blob = await response.blob();
        const svgPartial = await blob.slice(0, 512 * 1024).text();
        const svg = new SVG(svgPartial, blob);
        if (svg.id) {
          this.viewBox = svg.viewBox;
          this.svgProperties = Object.assign(attrsToObject(svg.el), filterAttrs(this.$attrs));
          this.children = svg.children;
        }
      } else {
        this.$emit('error', new ResponseError('Error fetching SVG', response));
      }
    },

    onError(error: unknown) {
      this.$emit('error', error);
    },

    startFetching() {
      this.fetchRetryer.startFetching();
      this.startTime = performance.now();
    },
  },

  render() {
    if (!this.svgProperties) {
      // Empty VNode.
      return h('svg');
    }

    performance.mark('svg_body_rendered');
    return h('svg',
      {...this.svgProperties},
    );
  },
});

</script>
